<script setup lang="ts">
import { showFailToast } from 'vant';
import LazyLoad from 'vanilla-lazyload';
import type { ILazyLoadInstance } from 'vanilla-lazyload';
import { initFingerprint, getUrlQuery, initYoumeng } from '@/utils';
import { fetchSiteJson } from '@/apis/site';
import { fetchDynamic, fetchDynamicComments } from '@/apis/dynamics';
import { useWxShareStore } from './store';

defineOptions({ name: 'DynamicApp' });

const wxshareStore = useWxShareStore();
let dynamicId: string;
const dynamicUser = ref<Dynamic.DynamicUser>();
const dynamicImages = ref<Dynamic.DynamicImage[]>([]);
const dynamicAudios = ref<Dynamic.DynamicAudio[]>([]);
const dynamicVideos = ref<Dynamic.DynamicVideo[]>([]);
const dynamicSubjects = ref<Dynamic.DynamicSubject[]>([]);
const dynamicContent = reactive<Dynamic.DynamicContentProps>({
  title: '',
  commentCount: 0,
  content: '',
  likeCount: 0,
  liked: false,
  publishTime: 0,
  type: '',
  uuid: '',
  address: '',
  longitude: 0,
  latitude: 0,
});
const siteInfo = reactive({
  downloadUrl: '',
  guidePic: '',
});
const dyanmicCommentContents = ref<Dynamic.DynamicCommentContent[]>([]);
const playingUUID = ref('');

const extinfo = ref('');

provide('dynamicUser', dynamicUser);
provide('dynamicImages', dynamicImages);
provide('dynamicAudios', dynamicAudios);
provide('dynamicVideos', dynamicVideos);
provide('dynamicContent', dynamicContent);
provide('dynamicSubjects', dynamicSubjects);
provide('siteInfo', siteInfo);
provide('dyanmicCommentContents', dyanmicCommentContents);
provide('playingUUID', playingUUID);
provide('extinfo', extinfo);

let lazyLoadInstance: ILazyLoadInstance;
const updateLazyLoad = () => {
  nextTick(() => {
    lazyLoadInstance.update();
  });
};
const getAllApis = async () => {
  const fingerprint = await initFingerprint();
  const [dynamicRes, commentRes, siteRes] = await Promise.all([
    fetchDynamic(dynamicId, fingerprint),
    fetchDynamicComments(dynamicId),
    fetchSiteJson(),
  ]);
  if (dynamicRes.code === 0) {
    const { data } = dynamicRes;
    if (data) {
      const { user, images, videos, audios, subjectList } = data;
      const { VITE_PROJECT_CONTENT_TYPE, VITE_DEFAULT_SHARE_IMAGE } = import.meta.env;
      dynamicUser.value = user;
      dynamicImages.value = images || [];
      dynamicAudios.value = audios || [];
      dynamicVideos.value = videos || [];
      dynamicSubjects.value = subjectList;
      dynamicContent.title = data.title || '';
      dynamicContent.commentCount = data.commentCount;
      dynamicContent.content = data.content;
      dynamicContent.likeCount = data.likeCount;
      dynamicContent.liked = data.liked;
      dynamicContent.publishTime = data.publishTime;
      dynamicContent.type = data.type;
      dynamicContent.uuid = data.uuid;
      dynamicContent.address = data.address;
      dynamicContent.longitude = data.longitude;
      dynamicContent.latitude = data.latitude;
      extinfo.value = JSON.stringify({
        id: dynamicContent.uuid,
        type: dynamicContent.type,
        contentType: VITE_PROJECT_CONTENT_TYPE,
        showType: 0,
      });
      const subjectNames = dynamicSubjects.value.map((item) => item.displayName).join('');
      let shareImage = VITE_DEFAULT_SHARE_IMAGE;
      if (dynamicImages.value.length > 0) {
        shareImage = dynamicImages.value[0].url_s || dynamicImages.value[0].url;
      } else if (dynamicVideos.value.length > 0) {
        shareImage = dynamicVideos.value[0].firstFrameUrl || shareImage;
      }
      wxshareStore.initWxShare({
        title: dynamicContent.title
          ? subjectNames + dynamicContent.title
          : dynamicContent.content,
        desc: '查看详情',
        imgUrl: shareImage,
      });
    }
  }
  siteInfo.downloadUrl = siteRes.downloadUrl;
  siteInfo.guidePic = siteRes.guidePic;
  if (commentRes.code === 0) {
    dyanmicCommentContents.value = [...commentRes.data.content];
  }
  updateLazyLoad();
};

onMounted(() => {
  initYoumeng();
  const query = getUrlQuery();
  if (query.dynamicId) {
    dynamicId = query.dynamicId;
    getAllApis();
    lazyLoadInstance = new LazyLoad();
  } else {
    showFailToast('动态不存在');
  }
});
</script>

<template>
  <section class="mx-auto h-screen max-w-screen-sm overflow-y-auto bg-white">
    <image-detail
      v-if="dynamicContent?.type === 'image' || dynamicContent?.type === 'text'"
    />
    <video-detail v-else-if="dynamicContent?.type === 'video'" />
    <audio-detail v-else-if="dynamicContent?.type === 'audio'" />
  </section>
</template>
